// src/components/inputs/SelectAutoComplacent.jsx
import { Autocomplete, TextField, useTheme } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React from 'react';
// import { makePreview } from '../../utils/helpers'; // ❌ لم نعد بحاجة لهذا هنا
import { useTranslation } from 'react-i18next';

const SelectAutoComplacent = ({
  name,
  value,
  options,
  onChange,
  disabled,
  sx,
  error,
  helperText
}) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const lng = i18n.language || 'en';
  const isArabic = lng === 'ar';

  // ✅ تعديل دالة جلب الاسم لإظهار النص كاملاً
  // ✅ تغليف الدالة بـ useCallback لمنع إعادة إنشائها في كل رندر وإصلاح تحذير الـ Hook
  const getLabel = React.useCallback((opt) => {
    if (!opt?.name) return '';

    // إذا كان الاسم نصاً مباشراً
    if (typeof opt.name === 'string') {
      return opt.name ?? '';
    }

    // إذا كان الاسم كائناً (متعدد اللغات)
    if (typeof opt.name === 'object') {
      const nameStr = isArabic
        ? (opt.name.ar || opt.name.en || '')
        : (opt.name.en || opt.name.ar || '');

      return nameStr ?? '';
    }

    return '';
  }, [isArabic]);

  // ✅ إضافة حالة للتحكم في النص المدخل يدوياً لمنع الـ refilling المزعج
  const [localInputValue, setLocalInputValue] = React.useState('');

  // مزامنة النص عند تغيير القيمة المختارة من الخارج
  React.useEffect(() => {
    const selectedOption = options?.find((o) => String(o.id) === String(value ?? ''));
    if (selectedOption) {
      setLocalInputValue(getLabel(selectedOption));
    } else if (!value) {
      setLocalInputValue('');
    }
  }, [value, options, getLabel]);

  return (
    <Autocomplete
      id={`${name}-autocomplete`}
      options={options ?? []}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, val) =>
        String(option?.id) === String(val?.id ?? val ?? '')
      }
      value={options?.find((o) => String(o.id) === String(value ?? '')) ?? null}
      inputValue={localInputValue}
      onInputChange={(event, newInputValue, reason) => {
        // نحدث الحالة المحلية فقط عند الكتابة أو المسح اليدوي
        if (reason === 'input' || reason === 'clear') {
          setLocalInputValue(newInputValue);
        }
      }}
      onChange={(_, newVal) => {
        if (!onChange) return;
        const syntheticEvent = {
          target: {
            name,
            value: newVal ? newVal.id : '',
          },
        };
        onChange(syntheticEvent);
        // عند الاختيار الفعلي، نتأكد من تحديث النص ليطابق الخيار
        if (newVal) {
          setLocalInputValue(getLabel(newVal));
        } else {
          setLocalInputValue('');
        }
      }}
      disabled={disabled}
      clearOnEscape
      disableClearable={false}
      popupIcon={<ArrowDropDownIcon />}
      sx={{
        width: '100%',
        mt: 1,
        minWidth: '80px',
        '& .MuiAutocomplete-endAdornment': {
          right: isArabic ? 'auto !important' : '9px !important',
          left: isArabic ? '9px !important' : 'auto !important',
        },
        '& .MuiAutocomplete-clearIndicator': {
          marginRight: isArabic ? 'auto' : undefined,
          marginLeft: isArabic ? 0 : 'auto',
        },
        '& .MuiAutocomplete-popupIndicator': {
          marginRight: isArabic ? 'auto' : undefined,
          marginLeft: isArabic ? 0 : 'auto',
        },
        ...sx,
      }}

      // ✅ تحسين عرض القائمة (List) لضمان التفاف النص الطويل (Wrapping)
      renderOption={(props, option) => {
        return (
          <li {...props} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {getLabel(option)}
          </li>
        );
      }}

      ListboxProps={{
        sx: {
          maxHeight: 320,
          direction: isArabic ? 'rtl' : 'ltr',
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          error={!!error}
          helperText={error || helperText}
          InputProps={{
            ...params.InputProps,
            sx: {
              height: 40,
              backgroundColor: theme.palette.background.input,
              borderRadius: 1,
              direction: isArabic ? 'rtl' : 'ltr',
              paddingRight: isArabic ? '9px !important' : '39px !important',
              paddingLeft: isArabic ? '39px !important' : '9px !important',
              '& input': {
                padding: '0 !important',
                textAlign: isArabic ? 'right' : 'left',
                '&::placeholder': {
                  textAlign: isArabic ? 'right' : 'left',
                },
              },
            },
          }}
        />
      )}
    />
  );
};

export default SelectAutoComplacent;